import mapValues from 'lodash/mapValues';

import { DocumentDataReviewField, DocumentDataReviewRequirementInstance } from '@evidentid/tprm-portal-lib/models/document-data-review';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

import { useDocumentDataReviewStore } from '@/modules/document-data-review/store';
import { createValidators } from '@/modules/document-data-review/utils/create-form-validators/createFormValidators';
import { buildRequirementFormId } from '@/modules/document-data-review/utils/requirement-form-id-utils/requirementFormIdUtils';
import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
import { FieldSchema } from '@/modules/dynamic-form/models/FieldSchema.model';
import { useDynamicFormStore } from '@/modules/dynamic-form/store';
import { createFieldMetaFromSchema } from '@/modules/dynamic-form/utils/create-field-meta-from-schema/createFieldMetaFromSchema';
import { createCompoundUnifiedFieldRecordValue } from '@/modules/dynamic-form/utils/form-field-helpers/formFieldHelpers';
import { toFieldPath } from '@/modules/dynamic-form/utils/to-field-path/toFieldPath';

function parseValuesBeforeNotFound(values: Record<FieldPath, any>, fieldsMeta: Record<FieldPath, FieldMeta>) {
    /*
     * We should not be storing null values in the valuesBeforeNotFound.
     * If they exist, they are a copy of the current value. In the case of initialization,
     * there are situations where auto-extracted or auto-saved null values appear.
     * Hence, we want to set them to default values for the fields — for now only `undefined`,
     * until we have type-specific default values.
     */
    return mapValues(values, ((fieldValue: any, path: FieldPath) => {
        if (fieldValue === null) {
            return fieldsMeta[toFieldPath(path)]?.defaultValue || undefined;
        }
        return fieldValue;
    }));
}

function initForm(params: {
    scope: any;
    ddrId: string;
    requirementType: TprmRequirementType;
    ddrFields: Record<FieldPath, DocumentDataReviewField | null>;
    requirementFieldsSchemas: FieldSchema[];
    rbacDisabled: boolean;
    requirementTypeComponentRef: Record<TprmRequirementType, any>;
}) {
    const {
        scope,
        ddrId,
        requirementType,
        ddrFields,
        requirementFieldsSchemas,
        rbacDisabled,
        requirementTypeComponentRef,
    } = params;

    const formId = buildRequirementFormId(ddrId, requirementType);

    const filteredSchemas: FieldSchema[] =
        requirementFieldsSchemas.filter((schema) => schema.path in ddrFields);
    const fieldsMeta: Record<FieldPath, FieldMeta> =
        filteredSchemas.reduce((acc: Record<FieldPath, FieldMeta>, schema: FieldSchema) => {
            acc[toFieldPath(schema.path)] = {
                ...createFieldMetaFromSchema(schema, { requirementType }),
                required: true,
            };
            return acc;
        }, {} as Record<FieldPath, FieldMeta>);

    const values =
        mapValues(ddrFields, ((fieldData: DocumentDataReviewField | null, path: FieldPath) => {
            if (fieldData === null) {
                return fieldsMeta[path]?.defaultValue || undefined;
            }
            return fieldData.value;
        }));
    const getPolicyExpirationDateValue =
        () => (useDynamicFormStore().forms[formId].values as Record<string, any>)['policy.expirationDate'] as string;
    const getPolicyEffectiveDateValue =
        () => (useDynamicFormStore().forms[formId].values as Record<string, any>)['policy.effectiveDate'] as string;
    const disabledFields = mapValues(
        ddrFields,
        (data: DocumentDataReviewField | null, path: FieldPath) => createCompoundUnifiedFieldRecordValue(
            fieldsMeta[path]?.defaultValue,
            rbacDisabled ? true : data?.value === null,
        ),
    );
    const ddrDisabledFields = rbacDisabled
        ? mapValues(ddrFields, (_, path: FieldPath) =>
            createCompoundUnifiedFieldRecordValue(fieldsMeta[path]?.defaultValue, true))
        : {};

    useDynamicFormStore().initializeForm({
        formId,
        values,
        fieldsMeta,
        fieldValidators: createValidators({
            schemas: filteredSchemas,
            fieldsMeta,
            getPolicyExpirationDateValue,
            getPolicyEffectiveDateValue,
        }),
        disabledFields: disabledFields as any,
    });

    useDocumentDataReviewStore().initializeDdrForm({
        formId,
        fields: ddrFields,
        hiddenFields: {},
        disabledFields: ddrDisabledFields as any,
        valuesBeforeNotFound: parseValuesBeforeNotFound(values, fieldsMeta),
    });
    useDocumentDataReviewStore().setRequirementTypeComponentRef({
        ddrId,
        requirementTypeComponentRef,
    });
}

export function initForms(params: {
    scope: any;
    ddrId: string;
    requirementsData: DocumentDataReviewRequirementInstance[];
    requirementsSchema: Record<TprmRequirementType, any>;
    rbacDisabled: boolean;
    requirementTypeComponentRef: Record<TprmRequirementType, any>;
}) {
    const {
        scope, ddrId, requirementsData, requirementsSchema, rbacDisabled, requirementTypeComponentRef,
    } = params;

    requirementsData.forEach((requirementData) => {
        initForm({
            scope,
            ddrId,
            requirementType: requirementData.requirement,
            ddrFields: requirementData.fields,
            requirementFieldsSchemas: requirementsSchema[requirementData.requirement].fields,
            rbacDisabled,
            requirementTypeComponentRef,
        });
    });
}
